/**
 * Modal กลางตัวเดียวสำหรับทะเบียน "ประเภทสินค้า" และ "หน่วยนับ" (`WO-P2-M11-016`,
 * D-135 ส่วนที่ 1) — `WO-P2-M11-017` เอาไปผูกกับหน้าสร้างสินค้า
 *
 * เส้นทางจริงคือ `router.py` บรรทัด ~1605-2010 (`product_type_router`,
 * `uom_router`): ประเภทสินค้ามีสถานะและปุ่มยกเลิก/เปิดใช้งาน (`APPROVE`),
 * หน่วยนับไม่มีคอลัมน์สถานะและไม่มี `DELETE`/transition เลย — `kind` จึงสลับ
 * เฉพาะสิ่งที่ต่างกันจริงสองเรื่องนี้ ส่วนรหัส/ชื่อ/การแก้ชื่อเดินทางเดียวกัน
 *
 * **สิทธิ์เป็น UX ไม่ใช่ด่านจริง** — `can(permissions, 'PRODUCT_MASTER', action)`
 * อ่าน action ตรงจากที่ router ต้องการของเส้นนั้น (`EDIT` สำหรับเพิ่ม/แก้ชื่อ,
 * `APPROVE` สำหรับยกเลิก/เปิดใช้งาน) ไม่มีการ derive จาก role เอง เซิร์ฟเวอร์เป็น
 * ผู้ตัดสินทุกคำขอเสมอ (`references/authorization-rbac.md` ข้อ 5)
 *
 * ยกเลิก/เปิดใช้งานต้องกดยืนยันซ้ำในแถว (`cap-confirm-bar`, D-129 ข้อ 3) — ไม่มี
 * `window.confirm` ที่ไหนในไฟล์นี้
 */

import { Fragment, useEffect, useState } from 'react';

import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  activateProductType,
  createProductType,
  createUom,
  deactivateProductType,
  fetchProductTypes,
  fetchUoms,
  renameProductType,
  renameUom,
  type ProductFailure,
} from '../../lib/products/client';
import {
  PRODUCT_MASTER_MODULE,
  PRODUCT_TYPE_CODE_MAX_LENGTH,
  REGISTER_NAME_MAX_LENGTH,
  UOM_CODE_MAX_LENGTH,
} from '../../lib/products/types';

export type ProductRegisterKind = 'type' | 'uom';

export interface ProductRegisterModalProps {
  readonly kind: ProductRegisterKind;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
  /** โหลดรายการใหม่สำเร็จ/แก้ชื่อ/ยกเลิก/เปิดใช้งานสำเร็จแล้ว — หน้าที่เรียกไปโหลด dropdown ของตัวเองใหม่ */
  readonly onRegisterChanged: () => void;
  /** เพิ่มสำเร็จ — ส่งรหัสที่เพิ่งสร้าง (ตัวพิมพ์ใหญ่) กลับให้ผู้เรียกเลือกใช้ทันที */
  readonly onCreated: (code: string) => void;
}

/** หนึ่งแถวของตาราง — `status` เป็น `null` สำหรับหน่วยนับ (ไม่มีคอลัมน์นั้น) */
interface RegisterRow {
  readonly code: string;
  readonly name: string;
  readonly status: string | null;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly rows: readonly RegisterRow[] }
  | ProductFailure;

type TransitionAction = 'deactivate' | 'activate';

interface ConfirmingTransition {
  readonly code: string;
  readonly action: TransitionAction;
}

/** ข้อความของความล้มเหลว — ใช้ `error.message`/`error.rule` ของเซิร์ฟเวอร์ก่อนเสมอ */
function registerFailureMessage(failure: ProductFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะทำรายการนี้ได้';
    case 'forbidden':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 403 — ไม่มีสิทธิ์ทำรายการนี้';
    case 'not-found':
      return failure.error.message ?? 'เซิร์ฟเวอร์ตอบ 404 — ไม่พบรายการนี้แล้ว โหลดรายการใหม่แล้วลองอีกครั้ง';
    case 'conflict':
      return (
        failure.error.message ??
        `เซิร์ฟเวอร์ตอบ 409${failure.error.rule === null ? '' : ` (${failure.error.rule})`}`
      );
    case 'rule-violation':
      return (
        failure.error.message ??
        `เซิร์ฟเวอร์ตอบ 422${failure.error.rule === null ? '' : ` (${failure.error.rule})`}`
      );
    case 'bad-request':
      return (
        failure.error.message ??
        `เซิร์ฟเวอร์ตอบ 400${failure.error.rule === null ? '' : ` (${failure.error.rule})`}`
      );
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus} — ลองใหม่อีกครั้ง`;
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'คำขอถูกยกเลิกก่อนได้รับคำตอบ';
  }
}

interface FailureNoticeProps {
  readonly failure: ProductFailure;
  readonly testId: string;
}

function RegisterFailureNotice({ failure, testId }: FailureNoticeProps) {
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';
  return (
    <div className="cap-notice" data-testid={testId} data-failure={failure.status} role="status">
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <p className="cap-notice__text">{registerFailureMessage(failure)}</p>
    </div>
  );
}

export function ProductRegisterModal({
  kind,
  open,
  onClose,
  permissions,
  apiBaseUrl,
  fetchImpl,
  onRegisterChanged,
  onCreated,
}: ProductRegisterModalProps) {
  const canAdd = can(permissions, PRODUCT_MASTER_MODULE, 'EDIT');
  const canApprove = can(permissions, PRODUCT_MASTER_MODULE, 'APPROVE');

  const [items, setItems] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [addPending, setAddPending] = useState(false);
  const [addFailure, setAddFailure] = useState<ProductFailure | null>(null);

  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editPending, setEditPending] = useState(false);
  const [editFailure, setEditFailure] = useState<ProductFailure | null>(null);

  const [confirmingTransition, setConfirmingTransition] = useState<ConfirmingTransition | null>(
    null,
  );
  const [transitionPending, setTransitionPending] = useState(false);
  const [transitionFailure, setTransitionFailure] = useState<ProductFailure | null>(null);

  // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (ค้างพิมพ์/แก้ไข/กำลังยืนยัน) ไม่ใช่ผลของครั้งนี้
  useEffect(() => {
    if (!open) {
      return;
    }
    setNewCode('');
    setNewName('');
    setAddPending(false);
    setAddFailure(null);
    setEditingCode(null);
    setEditingName('');
    setEditPending(false);
    setEditFailure(null);
    setConfirmingTransition(null);
    setTransitionPending(false);
    setTransitionFailure(null);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const controller = new AbortController();
    let active = true;

    setItems((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    const request =
      kind === 'type'
        ? fetchProductTypes({}, options).then((result): ListState =>
            result.status === 'ready'
              ? {
                  status: 'ready',
                  rows: result.page.items.map((entry) => ({
                    code: entry.type_code,
                    name: entry.name,
                    status: entry.status,
                  })),
                }
              : result,
          )
        : fetchUoms(options).then((result): ListState =>
            result.status === 'ready'
              ? {
                  status: 'ready',
                  rows: result.page.items.map((entry) => ({
                    code: entry.uom_code,
                    name: entry.name,
                    status: null,
                  })),
                }
              : result,
          );

    void request.then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setItems(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [open, kind, apiBaseUrl, fetchImpl, reloadToken]);

  const title = kind === 'type' ? 'จัดการประเภทสินค้า' : 'จัดการหน่วยนับ';
  const codeLabel = kind === 'type' ? 'รหัสประเภทสินค้า' : 'รหัสหน่วยนับ';
  const codeMaxLength = kind === 'type' ? PRODUCT_TYPE_CODE_MAX_LENGTH : UOM_CODE_MAX_LENGTH;
  const testIdPrefix = kind === 'type' ? 'product-type-register' : 'uom-register';
  const columnCount = kind === 'type' ? 4 : 3;

  const submitAdd = async () => {
    const code = newCode.trim();
    const name = newName.trim();
    if (addPending || code === '' || name === '' || !canAdd) {
      return;
    }
    setAddPending(true);
    setAddFailure(null);

    const result =
      kind === 'type'
        ? await createProductType({ type_code: code, name }, { apiBaseUrl, fetchImpl })
        : await createUom({ uom_code: code, name }, { apiBaseUrl, fetchImpl });

    setAddPending(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setAddFailure(result);
      return;
    }

    const createdCode = 'type_code' in result.entry ? result.entry.type_code : result.entry.uom_code;
    setReloadToken((token) => token + 1);
    onCreated(createdCode);
    onRegisterChanged();
    setNewCode('');
    setNewName('');
  };

  const openEdit = (row: RegisterRow) => {
    if (!canAdd) {
      return;
    }
    setEditingCode(row.code);
    setEditingName(row.name);
    setEditFailure(null);
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditingName('');
    setEditFailure(null);
  };

  const submitEdit = async () => {
    if (editingCode === null || editPending || !canAdd) {
      return;
    }
    const name = editingName.trim();
    if (name === '') {
      return;
    }
    setEditPending(true);
    setEditFailure(null);

    const result =
      kind === 'type'
        ? await renameProductType(editingCode, { name }, { apiBaseUrl, fetchImpl })
        : await renameUom(editingCode, { name }, { apiBaseUrl, fetchImpl });

    setEditPending(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setEditFailure(result);
      return;
    }

    setEditingCode(null);
    setEditingName('');
    setReloadToken((token) => token + 1);
    onRegisterChanged();
  };

  const openConfirm = (row: RegisterRow, action: TransitionAction) => {
    if (!canApprove) {
      return;
    }
    setConfirmingTransition({ code: row.code, action });
    setTransitionFailure(null);
  };

  const dismissConfirm = () => {
    setConfirmingTransition(null);
    setTransitionFailure(null);
  };

  const submitTransition = async () => {
    if (confirmingTransition === null || transitionPending || !canApprove) {
      return;
    }
    const row =
      items.status === 'ready'
        ? items.rows.find((candidate) => candidate.code === confirmingTransition.code)
        : undefined;
    const expectedStatus = row?.status ?? null;
    if (expectedStatus === null) {
      return;
    }

    setTransitionPending(true);
    setTransitionFailure(null);

    const result =
      confirmingTransition.action === 'deactivate'
        ? await deactivateProductType(confirmingTransition.code, expectedStatus, {
            apiBaseUrl,
            fetchImpl,
          })
        : await activateProductType(confirmingTransition.code, expectedStatus, {
            apiBaseUrl,
            fetchImpl,
          });

    setTransitionPending(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setTransitionFailure(result);
      return;
    }

    setConfirmingTransition(null);
    setReloadToken((token) => token + 1);
    onRegisterChanged();
  };

  const rows = items.status === 'ready' ? items.rows : [];
  const addDisabled = addPending || newCode.trim() === '' || newName.trim() === '';

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div data-testid={`${testIdPrefix}-body`}>
        {canAdd ? (
          <form
            className="cap-form"
            data-testid={`${testIdPrefix}-add-form`}
            aria-label={kind === 'type' ? 'เพิ่มประเภทสินค้าใหม่' : 'เพิ่มหน่วยนับใหม่'}
            onSubmit={(event) => {
              event.preventDefault();
              void submitAdd();
            }}
          >
            <label className="cap-field">
              <span className="cap-field__label">{codeLabel}</span>
              <input
                className="cap-input"
                data-testid={`${testIdPrefix}-add-code`}
                value={newCode}
                maxLength={codeMaxLength}
                autoComplete="off"
                onChange={(event) => {
                  setNewCode(event.target.value.toUpperCase());
                }}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ชื่อ</span>
              <input
                className="cap-input"
                data-testid={`${testIdPrefix}-add-name`}
                value={newName}
                maxLength={REGISTER_NAME_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setNewName(event.target.value);
                }}
              />
            </label>
            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid={`${testIdPrefix}-add-submit`}
                disabled={addDisabled}
              >
                {addPending ? 'กำลังเพิ่ม…' : 'เพิ่ม'}
              </button>
            </div>
            {addFailure === null ? null : (
              <RegisterFailureNotice failure={addFailure} testId={`${testIdPrefix}-add-failure`} />
            )}
          </form>
        ) : null}

        <section className="cap-card cap-card--table" aria-label={title}>
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              {kind === 'type' ? 'ประเภทสินค้าทั้งหมด' : 'หน่วยนับทั้งหมด'}
              {kind === 'type' ? (
                <InfoHint
                  label="เกี่ยวกับการยกเลิกใช้งาน"
                  text="ประเภทที่ยกเลิกใช้งานแล้วจะไม่แสดงให้เลือกในแบบฟอร์มสร้าง/แก้ไขสินค้า แต่สินค้าที่ใช้ประเภทนี้อยู่แล้วไม่ถูกกระทบ"
                />
              ) : null}
            </h2>
          </div>

          {items.status === 'loading' ? (
            <p className="cap-notice__text" data-testid={`${testIdPrefix}-loading`} role="status">
              กำลังโหลดรายการ…
            </p>
          ) : null}

          {items.status !== 'loading' && items.status !== 'ready' ? (
            <div className="cap-notice" data-testid={`${testIdPrefix}-list-failure`} role="status">
              <StatusBadge tone="danger">โหลดรายการไม่สำเร็จ</StatusBadge>
              <p className="cap-notice__text">{registerFailureMessage(items)}</p>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid={`${testIdPrefix}-list-retry`}
                onClick={() => {
                  setReloadToken((token) => token + 1);
                }}
              >
                ลองใหม่
              </button>
            </div>
          ) : null}

          {items.status === 'ready' && rows.length === 0 ? (
            <p className="cap-notice__text" data-testid={`${testIdPrefix}-empty`}>
              ยังไม่มีรายการในทะเบียนนี้
            </p>
          ) : null}

          {items.status === 'ready' && rows.length > 0 ? (
            <div className="cap-table-wrap">
              <table className="cap-table" data-testid={`${testIdPrefix}-table`}>
                <caption className="cap-visually-hidden">{title}</caption>
                <thead>
                  <tr>
                    <th scope="col">{codeLabel}</th>
                    <th scope="col">ชื่อ</th>
                    {kind === 'type' ? <th scope="col">สถานะ</th> : null}
                    <th scope="col">การกระทำ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isEditing = editingCode === row.code;
                    const isConfirming =
                      confirmingTransition !== null && confirmingTransition.code === row.code;
                    const nextAction: TransitionAction =
                      row.status === 'ACTIVE' ? 'deactivate' : 'activate';

                    return (
                      <Fragment key={row.code}>
                        <tr data-testid={`${testIdPrefix}-row-${row.code}`}>
                          <td>
                            <code className="cap-table__code">{row.code}</code>
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                className="cap-input"
                                data-testid={`${testIdPrefix}-edit-name-${row.code}`}
                                aria-label={`ชื่อใหม่ของ ${row.code}`}
                                value={editingName}
                                maxLength={REGISTER_NAME_MAX_LENGTH}
                                autoComplete="off"
                                onChange={(event) => {
                                  setEditingName(event.target.value);
                                }}
                              />
                            ) : (
                              row.name
                            )}
                          </td>
                          {kind === 'type' ? (
                            <td data-testid={`${testIdPrefix}-status-${row.code}`}>
                              <StatusBadge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
                                {row.status === 'ACTIVE' ? 'ใช้งาน' : 'ยกเลิกใช้งาน'}
                              </StatusBadge>
                            </td>
                          ) : null}
                          <td>
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="cap-button cap-button--primary"
                                  data-testid={`${testIdPrefix}-edit-save-${row.code}`}
                                  disabled={editPending || editingName.trim() === ''}
                                  onClick={() => {
                                    void submitEdit();
                                  }}
                                >
                                  {editPending ? 'กำลังบันทึก…' : 'บันทึก'}
                                </button>{' '}
                                <button
                                  type="button"
                                  className="cap-button cap-button--ghost"
                                  data-testid={`${testIdPrefix}-edit-cancel-${row.code}`}
                                  disabled={editPending}
                                  onClick={cancelEdit}
                                >
                                  ยกเลิก
                                </button>
                              </>
                            ) : canAdd ? (
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid={`${testIdPrefix}-edit-open-${row.code}`}
                                onClick={() => {
                                  openEdit(row);
                                }}
                              >
                                แก้ชื่อ
                              </button>
                            ) : null}
                            {kind === 'type' && canApprove && !isEditing ? (
                              confirmingTransition !== null &&
                              confirmingTransition.code === row.code ? (
                                <div
                                  className="cap-confirm-bar"
                                  data-testid={`${testIdPrefix}-confirm-${row.code}`}
                                >
                                  <span className="cap-confirm-bar__text">
                                    ยืนยัน
                                    {confirmingTransition.action === 'deactivate'
                                      ? 'ยกเลิกใช้งาน'
                                      : 'เปิดใช้งาน'}
                                    &nbsp;{row.code}?
                                  </span>
                                  <button
                                    type="button"
                                    className="cap-button cap-button--primary"
                                    data-testid={`${testIdPrefix}-confirm-${row.code}-yes`}
                                    disabled={transitionPending}
                                    onClick={() => {
                                      void submitTransition();
                                    }}
                                  >
                                    {transitionPending ? 'กำลังส่ง…' : 'ยืนยัน'}
                                  </button>
                                  <button
                                    type="button"
                                    className="cap-button cap-button--ghost"
                                    data-testid={`${testIdPrefix}-confirm-${row.code}-no`}
                                    disabled={transitionPending}
                                    onClick={dismissConfirm}
                                  >
                                    ไม่ทำ
                                  </button>
                                </div>
                              ) : (
                                <>
                                  {' '}
                                  <button
                                    type="button"
                                    className="cap-button cap-button--ghost"
                                    data-testid={`${testIdPrefix}-transition-open-${row.code}`}
                                    onClick={() => {
                                      openConfirm(row, nextAction);
                                    }}
                                  >
                                    {row.status === 'ACTIVE' ? 'ยกเลิกใช้งาน' : 'เปิดใช้งาน'}
                                  </button>
                                </>
                              )
                            ) : null}
                          </td>
                        </tr>
                        {isEditing && editFailure !== null ? (
                          <tr data-testid={`${testIdPrefix}-edit-failure-row-${row.code}`}>
                            <td colSpan={columnCount}>
                              <RegisterFailureNotice
                                failure={editFailure}
                                testId={`${testIdPrefix}-edit-failure-${row.code}`}
                              />
                            </td>
                          </tr>
                        ) : null}
                        {isConfirming && transitionFailure !== null ? (
                          <tr data-testid={`${testIdPrefix}-transition-failure-row-${row.code}`}>
                            <td colSpan={columnCount}>
                              <RegisterFailureNotice
                                failure={transitionFailure}
                                testId={`${testIdPrefix}-transition-failure-${row.code}`}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
